import { computed } from 'vue';
import getCareTypeOptions from '@/helpers/careTypeOptions';
import { encodeCursor } from '@/helpers/pagination';
import { useFavoritesQuery } from './useFavoritesQuery';
import { useFavoritesRoute } from './useFavoritesRoute';
import { useFavoriteRemoval } from './useFavoriteRemoval';
import type { FavoriteSittersVariables } from './types';

const ITEMS_PER_PAGE = 20;

export function useFavorites() {
  const { careType, currentPage, applyCareTypeFilter, handlePageChange, correctPage } = useFavoritesRoute(ITEMS_PER_PAGE);
  const variables = computed<FavoriteSittersVariables>(() => ({
    careType: careType.value,
    first: ITEMS_PER_PAGE,
    after: currentPage.value > 1 ? encodeCursor((currentPage.value - 1) * ITEMS_PER_PAGE) : null,
  }));
  const { connection, isLoading, hasError, refreshError, refreshFavorites, onLoaded } = useFavoritesQuery(variables);
  const {
    visibleEdges,
    totalCount,
    removingSitterId,
    favoriteActionError,
    removedSitterName,
    showRemovedNotification,
    removeFavorite,
    hideRemovedNotification,
  } = useFavoriteRemoval(connection, variables, refreshFavorites);
  const availableCareTypes = computed(() => connection.value?.availableCareTypes ?? []);

  onLoaded(() => correctPage(totalCount.value));

  const favorites = computed(() =>
    visibleEdges.value.map(({ node: { __typename: _typename, hasConversation, caretype, ...cardProps } }) => ({
      cardProps: { ...cardProps, caretype: caretype?.toLowerCase() ?? '' },
      hasConversation,
    }))
  );
  const careTypeOptions = computed(() => [
    { value: 'ALL', label: 'All' },
    ...getCareTypeOptions()
      .filter((option) => availableCareTypes.value.includes(option.value))
      .map((option) => ({ value: option.value, label: option.display })),
  ]);
  const filterLabel = computed(
    () => getCareTypeOptions().find((option) => option.value === careType.value)?.display ?? 'Care type'
  );
  const showFilter = computed(() => availableCareTypes.value.length > 0 || careType.value !== null);

  const showPagination = computed(() => !isLoading.value && totalCount.value > ITEMS_PER_PAGE);

  return {
    isLoading,
    hasError,
    refreshError,
    favorites,
    careType,
    totalCount,
    careTypeOptions,
    filterLabel,
    applyCareTypeFilter,
    currentPage,
    itemsPerPage: ITEMS_PER_PAGE,
    removingSitterId,
    favoriteActionError,
    showRemovedNotification,
    removedSitterName,
    showFilter,
    showPagination,
    handlePageChange,
    removeFavorite,
    hideRemovedNotification,
  };
}
